/**
 * Session-scoped audio store (IndexedDB).
 *
 * Keeps patient-file audio blobs alive across tab switches and patient
 * navigation within the SAME working day. Entries are wiped:
 *   1. When the user clicks "Sair" (logout) — see AuthContext.logout.
 *   2. On next login, any entry whose `date` is not today is purged
 *      (failsafe for when the user closes the browser without logging out).
 *
 * Scope: ONLY audio captured inside the patient file (PatientSessionPanel
 * and PatientRecordsTab). The avulso "Audio AI" page and telemedicine
 * streams are NOT persisted here.
 *
 * Nothing is ever uploaded to Firebase — IndexedDB is a local, per-browser
 * store. Audios therefore never leave the doctor's computer.
 */

const DB_NAME = 'dratento-session-audio';
const DB_VERSION = 1;
const STORE = 'audios';

/** Kind of audio slot within a patient file. */
export type AudioSlot = 'session' | 'prontuario';

interface AudioRecord {
  /** `${userId}::${patientId}::${slot}` — deterministic so we can overwrite. */
  id: string;
  userId: string;
  patientId: string;
  slot: AudioSlot;
  /** Local YYYY-MM-DD date when the audio was last written. */
  date: string;
  /** Single blob (legacy) or array of blobs (multi-audio). */
  blob?: Blob;
  blobs?: Blob[];
  updatedAt: number;
}

/** Local YYYY-MM-DD (not UTC). Used to decide what "today" means. */
export function todayLocalISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function makeId(userId: string, patientId: string, slot: AudioSlot): string {
  return `${userId}::${patientId}::${slot}`;
}

/** Returns null if IndexedDB is unavailable (private mode, old browser). */
function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'id' });
        os.createIndex('userId', 'userId', { unique: false });
        os.createIndex('date', 'date', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      console.warn('[audioSessionStore] Falha ao abrir IndexedDB:', req.error);
      resolve(null);
    };
  });
}

function tx(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE, mode).objectStore(STORE);
}

/** Persist blob(s) (overwrites any prior audio for the same patient+slot). */
export async function putAudio(
  userId: string,
  patientId: string,
  slot: AudioSlot,
  blobOrBlobs: Blob | Blob[],
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    const rec: AudioRecord = {
      id: makeId(userId, patientId, slot),
      userId,
      patientId,
      slot,
      date: todayLocalISO(),
      blobs: Array.isArray(blobOrBlobs) ? blobOrBlobs : [blobOrBlobs],
      updatedAt: Date.now(),
    };
    const req = tx(db, 'readwrite').put(rec);
    req.onsuccess = () => {
      db.close();
      resolve();
    };
    req.onerror = () => {
      console.warn('[audioSessionStore] put falhou:', req.error);
      db.close();
      resolve();
    };
  });
}

/** Returns the stored blobs or null. Only returns today's audio. */
export async function getAudio(
  userId: string,
  patientId: string,
  slot: AudioSlot,
): Promise<Blob[] | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    const req = tx(db, 'readonly').get(makeId(userId, patientId, slot));
    req.onsuccess = () => {
      const rec = req.result as AudioRecord | undefined;
      db.close();
      if (!rec) {
        resolve(null);
        return;
      }
      if (rec.date !== todayLocalISO()) {
        resolve(null);
        return;
      }
      // Backwards-compat: old records have a single `blob` field.
      if (rec.blobs && rec.blobs.length > 0) {
        resolve(rec.blobs);
      } else if (rec.blob) {
        resolve([rec.blob]);
      } else {
        resolve(null);
      }
    };
    req.onerror = () => {
      db.close();
      resolve(null);
    };
  });
}

/** Remove one audio entry (used when the doctor clicks "Descartar"). */
export async function deleteAudio(
  userId: string,
  patientId: string,
  slot: AudioSlot,
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    const req = tx(db, 'readwrite').delete(makeId(userId, patientId, slot));
    req.onsuccess = () => {
      db.close();
      resolve();
    };
    req.onerror = () => {
      db.close();
      resolve();
    };
  });
}

/** Wipe EVERY audio blob in the store. Called from logout. */
export async function clearAllAudios(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    const req = tx(db, 'readwrite').clear();
    req.onsuccess = () => {
      db.close();
      resolve();
    };
    req.onerror = () => {
      db.close();
      resolve();
    };
  });
}

/**
 * Delete every record whose date is not today OR whose userId is different
 * from the current user. Called once on app boot / login as a failsafe
 * (user closed the browser without clicking Sair, or a different user
 * logs in on the same machine).
 */
export async function purgeStaleAudios(currentUserId: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  const today = todayLocalISO();
  return new Promise((resolve) => {
    const store = tx(db, 'readwrite');
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        db.close();
        resolve();
        return;
      }
      const rec = cursor.value as AudioRecord;
      if (rec.date !== today || rec.userId !== currentUserId) {
        cursor.delete();
      }
      cursor.continue();
    };
    req.onerror = () => {
      db.close();
      resolve();
    };
  });
}
